import {
  appFormUtil,
  gqlCacheUtil
} from 'modules/common';
import React, { useEffect } from 'react';

import {
  useSaveClientReportMutation,
  SaveClientReport_Input,
  useClientReportFormQuery,
  ClientReportDetailSectionQuery,
  SaveClientReportMutation,
  ClientReportDetailSectionDocument,
  ClientReportTableQuery,
  ClientReportTableDocument
} from 'generated/graphql';
import _ from 'lodash';

export type IFormState = {
  approved: boolean;
  disableApproval: boolean;
  attachments: string[];
  description: string;
  id: string;
  metaDeliveries: string[];
  metaInspections: string[];
  metaMilestones: string[];
  metaPhotos: string[];
  projectId: string;
};

const initialState = {
  approved: false,
  disableApproval: false,
  attachments: '',
  description: '',
  id: '',
  metaDeliveries: '',
  metaInspections: '',
  metaMilestones: '',
  metaPhotos: '',
  projectId: ''
};

export function useFormState(id: string, projectId: string) {
  const { formState, dispatch, genFieldProps } = appFormUtil.useFormState({
    initialState,
    key2FieldConfig: {
      approved: {
        label: 'Approved',
        type: 'checkbox'
      },
      attachments: {
        label: 'Attachments',
        type: 'text'
      },
      description: {
        label: 'Description',
        type: 'text'
      },
      id: {
        label: 'Id',
        type: 'text'
      },
      metaDeliveries: {
        label: 'Meta deliveries',
        type: 'text'
      },
      metaInspections: {
        label: 'Meta inspections',
        type: 'text'
      },
      metaMilestones: {
        label: 'Meta milestones',
        type: 'text'
      },
      metaPhotos: {
        label: 'Meta photos',
        type: 'text'
      },
      projectId: {
        label: 'Project id',
        type: 'text'
      }
    }
  });

  useEffect(() => {
    if (projectId !== '' && formState.projectId === '') {
      dispatch({
        type: 'UPDATE',
        payload: {
          property: 'projectId',
          value: projectId
        }
      });
    }
    return () => { };
  }, [
    dispatch,
    formState.projectId,
    projectId
  ]);

  const { data, loading, called } = useQuery(id);

  useEffect(() => {
    if (formState.id === '' && data.id !== '' && called && !loading) {
      dispatch({
        type: 'BATCH_UPDATE',
        payload: {
          ...formState,
          ...data
        }
      });
    }
  }, [
    called,
    data,
    dispatch,
    formState,
    loading
  ]);

  return React.useMemo(
    () => {

      return {
        formState,
        dispatch,
        genFieldProps
      };
    },
    [
      dispatch,
      formState,
      genFieldProps
    ]
  );
}

export function useSaveClientReport() {
  const [
    fnMutate,
    tuple
  ] = useSaveClientReportMutation({
    refetchQueries: []
  });
  const fnWrappedMutate = React.useCallback(
    async (input: SaveClientReport_Input) => {
      await fnMutate({
        variables: {
          input
        },
        updateQueries: {
          ...gqlCacheUtil.genUpdateQueryMap<ClientReportDetailSectionQuery, SaveClientReportMutation>(ClientReportDetailSectionDocument, (prevState, { mutationResult }) => {
            if (!prevState.clientReport) {
              return prevState;
            }
            if (!mutationResult.data?.saveClientReport?.id) {
              return prevState;
            }
            if (mutationResult.data.saveClientReport.id !== prevState.clientReport.id) {
              return prevState;
            }
            return {
              ...prevState,
              clientReport: {
                ...prevState.clientReport,
                description: mutationResult.data.saveClientReport.description,
                approvedAt: mutationResult.data.saveClientReport.approvedAt,
                approvedBy: mutationResult.data.saveClientReport.approvedBy
              }
            };

          }),
          ...gqlCacheUtil.genUpdateQueryMap<ClientReportTableQuery, SaveClientReportMutation>(ClientReportTableDocument, (prevState, { mutationResult, queryVariables }) => {
            if (!prevState.clientReports) {
              return prevState;
            }

            if (queryVariables.id !== mutationResult.data?.saveClientReport?.id) {
              return {
                ...prevState,
                clientReports: _.compact([
                  mutationResult.data?.saveClientReport,
                  ...prevState.clientReports
                ])
              };
            }
            const updatedReports = prevState.clientReports.map((node) => {
              if (node.id === mutationResult.data?.saveClientReport?.id) {
                return {
                  ...node,
                  ...mutationResult.data.saveClientReport
                };
              }
              return node;
            });
            return {
              ...prevState,
              clientReports: updatedReports
            };
          })
        }
      });
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}

export interface IData {
  id: string;
  description: string;
  approved: boolean;
  disableApproval: boolean;
}

export function useQuery(id: string) {
  const tuple = useClientReportFormQuery({
    variables: {
      id
    },
    skip: id === ''
  });

  return React.useMemo(
    () => {
      const data: IData = {
        id: tuple.data?.clientReport?.id ?? '',
        description: tuple.data?.clientReport?.description ?? '',
        approved: !_.isNull(tuple.data?.clientReport?.approvedAt) ? true : false,
        disableApproval: !_.isNull(tuple.data?.clientReport?.approvedAt) ? true : false

      };

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}
